import React, { useEffect, useState, useRef } from "react";
import { useGetProductsMutation } from "../../api/api";
import {
  IsServiceTypeVendor,
  useDecodedTokenResult,
  formatDateTime,
  getVendorTimezone,
} from "../../lib/utils";
import { ChevronLeft, XCircle } from "lucide-react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { MdOutlineLiveTv } from "react-icons/md";
import { CreateUpdateLiveStreamSession } from "./CreateUpdateLiveStreamSession";
import ProductSearch from "./components/ProductSearch";
import StreamScheduler from "./components/StreamScheduler";
import ProductArrangement from "./components/ProductArrangement";

interface Data {
  productId: number;
  defaultImageUrl: string;
  productName: string;
  sku: string;
  category: string;
  subCategory: string;
  quantityAvailable: number;
  price: number;
  oldPrice: number;
  status: string;
  createdAt: string;
}

interface DropDown {
  image: string;
  name: string;
  quantity: number;
  productId: number;
}

interface Props {
  sessionToReschedule?: {
    id: number;
    title: string;
    startDateTime: string;
    products: Array<{
      productId: number;
      defaultImageUrl: string;
      productName: string;
      quantityAvailable: number;
    }>;
  } | null;
  onSessionUpdated?: () => void;
  onBack?: () => void;
  refetchSessionsOnUpdate?: () => void;
}

const LiveStreamProductsSelection: React.FC<Props> = ({
  sessionToReschedule,
  onSessionUpdated,
  onBack,
  refetchSessionsOnUpdate,
}) => {
  const vendorId = useDecodedTokenResult().vendorId!;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [data, setData] = useState<Data[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [selectedProducts, setSelectedProducts] = useState<DropDown[]>([]);
  const [getProducts, { isLoading: isLoadingMutation, isError, error }] =
    useGetProductsMutation();
  const [selectedDateTime, setSelectedDateTime] = useState<string>("");
  const isServiceTypeVendor = IsServiceTypeVendor();
  const [title, setTitle] = useState<string>("");
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [removedProductIds, setRemovedProductIds] = useState<number[]>([]);
  const [originalProductIds, setOriginalProductIds] = useState<Set<number>>(
    new Set()
  );
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const isMobile = window.innerWidth < 768;

  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const response: any = await getProducts({
        getProductReqDto: {
          vendorId: vendorId,
          searchQuery: searchTerm || undefined,
          pageNumber,
          pageSize,
          getTotalCount: true,
          sortingOrder: "asc",
          sortingColumn: "createdAt",
        },
      }).unwrap();

      setTimeout(() => {
        setData(response?.data?.products || []);
        setTotalCount(response?.data?.totalCount || 0);
        setIsLoadingProducts(false);
      }, 100);
    } catch (error) {
      console.log(error);
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, pageNumber, pageSize]);

  const formatDateTimeForInput = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString);
      return formatDateTime(date, "YYYY-MM-DDTHH:mm:ssZ");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  useEffect(() => {
    if (sessionToReschedule) {
      setTitle(sessionToReschedule.title);
      setSelectedDateTime(
        formatDateTimeForInput(sessionToReschedule.startDateTime)
      );

      const sessionProducts: DropDown[] = sessionToReschedule.products.map(
        (product) => ({
          image: product.defaultImageUrl,
          name: product.productName,
          quantity: product.quantityAvailable,
          productId: product.productId,
        })
      );

      // Store original product IDs
      setOriginalProductIds(
        new Set(sessionToReschedule.products.map((p) => p.productId))
      );
      setSelectedProducts(sessionProducts);
    }
  }, [sessionToReschedule]);

  const isProductSelected = React.useMemo(() => {
    const selectedIds = new Set(selectedProducts.map((p) => p.productId));
    return (productId: number) => selectedIds.has(productId);
  }, [selectedProducts]);

  const toggleProductSelection = (product: Data, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const isSelected = selectedProducts.some(
      (p) => p.productId === product.productId
    );

    setSelectedProducts((prevProducts) => {
      if (isSelected) {
        return prevProducts.filter((p) => p.productId !== product.productId);
      } else {
        const newProduct = {
          image: product.defaultImageUrl,
          name: product.productName,
          quantity: product.quantityAvailable,
          productId: product.productId,
        };
        return [...prevProducts, newProduct];
      }
    });
  };

  const removeProduct = (product: DropDown, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProducts((prevProducts) =>
      prevProducts.filter((p) => p.productId !== product.productId)
    );

    if (sessionToReschedule) {
      setRemovedProductIds((prev) => [...prev, product.productId]);
    }
  };

  const moveProduct = (dragIndex: number, hoverIndex: number) => {
    setSelectedProducts((prevProducts) => {
      const newProducts = [...prevProducts];
      const draggedProduct = newProducts[dragIndex];
      newProducts.splice(dragIndex, 1);
      newProducts.splice(hoverIndex, 0, draggedProduct);
      return newProducts;
    });
  };

  const resetForm = () => {
    setSearchTerm("");
    setSelectedProducts([]);
    setTitle("");
    setSelectedDateTime("");
    setIsCreatingSession(false);
    setRemovedProductIds([]);
    setOriginalProductIds(new Set());
    setIsSessionLoading(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleGoLive = () => {
    setIsSessionLoading(true);
    setIsCreatingSession(true);
  };

  const handleSuccess = () => {
    setIsSessionLoading(false);
    resetForm();
    onSessionUpdated?.();
  };

  // Get only new product IDs that weren't in the original session
  const getNewProductIds = () => {
    if (!sessionToReschedule) {
      return selectedProducts.map((p) => p.productId);
    }
    return selectedProducts
      .map((p) => p.productId)
      .filter((id) => !originalProductIds.has(id));
  };

  const handleClearProducts = () => {
    setSelectedProducts([]);
    setRemovedProductIds([]);
    setTitle("");
    setSelectedDateTime("");
    if (sessionToReschedule) {
      setRemovedProductIds(
        sessionToReschedule.products.map((product) => product.productId)
      );
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto mb-10">
        <div
          className={`max-w-5xl mx-auto px-6 py-8 relative bg-gray-50 overflow-y-auto ${
            isMobile ? "mb-20 pb-10" : ""
          }`}
        >
          {/* Mobile Back Button */}
          <div className="md:hidden absolute top-4 left-4 z-10">
            <button
              onClick={onBack}
              className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          </div>

          <div className="flex w-fit bg-customGreenLight p-2 rounded-full mb-4 mx-auto">
            <MdOutlineLiveTv className="w-10 h-10 text-customGreenColor" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 text-center">
            Create Live Stream
          </h1>
          <p className="text-gray-600 text-center mb-12">
            Schedule your live streaming session and optionally add products to
            showcase
          </p>

          <DndProvider backend={HTML5Backend}>
            <div className="space-y-8">
              {/* Step 1: Product Selection */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-customGreenColor text-white rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-semibold text-gray-800">
                          Add Products
                        </h2>
                        <span className="text-gray-500 text-sm">
                          (Optional)
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1">
                        Search and select products if you want to showcase them
                        during your live stream. You can also create a session
                        without products.
                      </p>
                    </div>
                  </div>

                  {/* Clear Products Button */}
                  {selectedProducts.length > 0 && (
                    <button
                      onClick={handleClearProducts}
                      className="flex items-center gap-2 text-gray-500 hover:text-red-500 transition-colors"
                    >
                      <XCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">Clear All</span>
                    </button>
                  )}
                </div>

                <ProductSearch
                  searchTerm={searchTerm}
                  isOpen={isOpen}
                  isLoading={isLoadingProducts}
                  isError={isError}
                  data={data}
                  isServiceTypeVendor={isServiceTypeVendor}
                  isProductSelected={isProductSelected}
                  onSearchChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onProductSelect={toggleProductSelection}
                  setIsOpen={setIsOpen}
                />
              </div>

              {selectedProducts.length > 0 && (
                <>
                  {/* Step 2: Arrange Products */}
                  <ProductArrangement
                    selectedProducts={selectedProducts}
                    onClearProducts={handleClearProducts}
                    moveProduct={moveProduct}
                    removeProduct={removeProduct}
                  />
                </>
              )}

              {selectedProducts.length === 0 && (
                <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-12 text-center">
                  <div className="text-gray-500 text-lg">
                    You can add products using the search box above, or continue
                    without products
                  </div>
                </div>
              )}

              {/* Step 3: Schedule and Go Live */}
              <StreamScheduler
                title={title}
                selectedDateTime={selectedDateTime}
                onTitleChange={(e) => setTitle(e.target.value)}
                onDateTimeChange={(e) => setSelectedDateTime(e.target.value)}
                onSchedule={handleGoLive}
                isRescheduling={!!sessionToReschedule}
              />
            </div>
          </DndProvider>
        </div>
      </div>

      {isCreatingSession && (
        <CreateUpdateLiveStreamSession
          title={title}
          startDateTime={selectedDateTime}
          productIds={getNewProductIds()}
          vendorTimezone={getVendorTimezone()}
          closeSession={resetForm}
          isLoading={isSessionLoading}
          sessionId={sessionToReschedule?.id}
          removeProductIds={removedProductIds}
          onSuccess={handleSuccess}
          refetchSessionsOnUpdate={refetchSessionsOnUpdate}
        />
      )}
    </div>
  );
};

export default LiveStreamProductsSelection;
